import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

import type { DemandChange, DemandTrendRow } from '../models/productInsights.types'
import {
  demandTrendRows,
  filterByChangeType,
  sortDemandTrendRows,
} from '../service/ecommerce/demandTrend'

import {
  useReportData,
  useReportSchedules,
  type ReportDataState,
  type ReportSpec,
  type ScheduleTab,
} from '@/features/reports'

// Demand Trend list state — v3 `ProductInsightsTopProducts.vue` (ORP-18953), two defects FIXED
// with product sign-off carried by the ticket:
//   D2 — v3 never assigned `pageCount`, so the pager could never leave page 1; rows 10+ were
//        unreachable. Here `pageCount` derives from the filtered length.
//   D3 — v3 `watch(() => selectedType, …)` watched the ref OBJECT, so the page never reset on a
//        RISER/SINKER change. Here the watch observes the value.
// The headless factory is separated from the store-coupled shell so both fixes are testable
// without Pinia (same seam pattern as `createCommerceNavVisibility`).

export const DEMAND_TREND_PAGE_SIZE = 9
const DEMAND_TREND_REPORT_ID = 17
/** Canonical product-level partition, approved in ORP-18953's scoped follow-up. */
const DEMAND_TREND_TAB = 'product_bestsellers'

export type DemandChangeFilter = Exclude<DemandChange, 'FLAT'> | null

export interface DemandTrendView {
  changeType: Ref<DemandChangeFilter>
  sortField: Ref<string>
  sortOrder: Ref<1 | -1>
  page: Ref<number>
  pageCount: ComputedRef<number>
  totalRows: ComputedRef<number>
  /** The visible 9-row slice: filtered → sorted → paged. */
  rows: ComputedRef<DemandTrendRow[]>
  /** v3 `changeSortField`: a new field starts descending; the same field toggles. */
  setSort: (field: string) => void
}

/** Headless filter/sort/page state over an injected row source (no store coupling). */
export function createDemandTrendView(source: () => readonly DemandTrendRow[]): DemandTrendView {
  const changeType = ref<DemandChangeFilter>(null)
  const sortField = ref('product_rank')
  const sortOrder = ref<1 | -1>(-1)
  const page = ref(1)

  const filtered = computed(() => filterByChangeType(demandTrendRows(source()), changeType.value))
  const sorted = computed(() =>
    sortDemandTrendRows(filtered.value, sortField.value, sortOrder.value),
  )
  const pageCount = computed(() =>
    Math.max(1, Math.ceil(filtered.value.length / DEMAND_TREND_PAGE_SIZE)),
  )
  const rows = computed(() => {
    const start = (page.value - 1) * DEMAND_TREND_PAGE_SIZE
    return sorted.value.slice(start, start + DEMAND_TREND_PAGE_SIZE)
  })

  // D3: reset on filter change; the clamp keeps a shrunken result set reachable (part of D2 —
  // a working pager must not strand the reader past the last page).
  watch(changeType, () => {
    page.value = 1
  })
  watch(pageCount, (count) => {
    if (page.value > count) page.value = count
  })

  const setSort = (field: string): void => {
    if (sortField.value !== field) sortOrder.value = -1
    else sortOrder.value = sortOrder.value === -1 ? 1 : -1
    sortField.value = field
  }

  return {
    changeType,
    sortField,
    sortOrder,
    page,
    pageCount,
    totalRows: computed(() => filtered.value.length),
    rows,
    setSort,
  }
}

export interface UseDemandTrend extends DemandTrendView {
  state: Ref<ReportDataState>
  reload: () => Promise<void>
}

/** The store-coupled shell: schedule → `ReportSpec` → 08's `useReportData` → the view. */
export function useDemandTrend(): UseDemandTrend {
  const schedules = useReportSchedules()

  const scheduleTab = computed<ScheduleTab | null>(() => {
    const selection = schedules.checkScheduleTabs(DEMAND_TREND_REPORT_ID, DEMAND_TREND_TAB)
    const first = Array.isArray(selection) ? selection[0] : selection
    return typeof first === 'object' && first !== null ? first : null
  })

  const spec = computed<ReportSpec | null>(() => {
    const partition = scheduleTab.value?.partition_name
    if (!partition) return null
    return {
      report_id: DEMAND_TREND_REPORT_ID,
      dimensions: ['product_name', 'relative_demand', 'relative_demand_change'],
      pagination_enabled: false,
      partition_name: partition,
    }
  })

  const data = useReportData(spec)
  const view = createDemandTrendView(() => data.rows.value as DemandTrendRow[])
  return { ...view, state: data.state, reload: data.reload }
}
